import { useState, useRef, useEffect } from 'react';
import { NFTSearchInput } from './components/nft';
import { BannerCropModal } from './components/crop';
import { BannerBuilder } from './components/banner';
import { MemeBuilder } from './components/meme';
import { FunLoadingText } from './components/common/FunLoadingText';
import { ThemeToggle } from './components/common/ThemeToggle';
import { useNFTStore, useMemeStore, useThemeStore } from './stores';
import { playNavSound } from './utils/gameSound';

type AppTab = 'pfp' | 'memes' | 'banner';

const TAB_ORDER: AppTab[] = ['pfp', 'memes', 'banner'];

function App() {
  const [activeTab, setActiveTab] = useState<AppTab>('pfp');
  const prevTabRef = useRef<AppTab>('pfp');
  const { currentNFT, isLoading, error } = useNFTStore();
  const { addNFTElement: addToMeme } = useMemeStore();
  const { theme } = useThemeStore();

  // Track if user has ever loaded an NFT (to show full UI)
  const hasLoadedNFT = currentNFT !== null;

  // Header exit animation state
  const [headerVisible, setHeaderVisible] = useState(!hasLoadedNFT);
  const [headerExiting, setHeaderExiting] = useState(false);

  // Trigger header exit animation when NFT has loaded (not on search start)
  useEffect(() => {
    if (hasLoadedNFT && headerVisible && !headerExiting) {
      setHeaderExiting(true);
      const timer = setTimeout(() => {
        setHeaderVisible(false);
        setHeaderExiting(false);
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [hasLoadedNFT, headerVisible, headerExiting]);

  // Apply theme class to document on mount and when theme changes
  useEffect(() => {
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
    document.documentElement.setAttribute('dir', 'ltr');
    document.body.setAttribute('dir', 'ltr');
  }, [theme]);

  // Auto-navigate to memes tab when NFT is loaded
  useEffect(() => {
    if (currentNFT && !isLoading) {
      handleTabChange('memes');
    }
  }, [currentNFT, isLoading]);

  const handleTabChange = (newTab: AppTab) => {
    // Play navigation sound when switching tabs
    if (newTab !== activeTab) {
      playNavSound();
    }

    prevTabRef.current = activeTab;

    // Auto-add NFT to meme canvas when switching to memes tab
    if (newTab === 'memes' && currentNFT) {
      const { nftElements } = useMemeStore.getState();
      // Only add if not already in the canvas
      const alreadyInCanvas = nftElements.some(el => el.nftData.mintAddress === currentNFT.mintAddress);
      if (!alreadyInCanvas) {
        addToMeme(currentNFT);
      }
    }

    setActiveTab(newTab);
  };

  // Get panel position class based on tab order
  const getPanelClass = (tab: AppTab): string => {
    const activeIndex = TAB_ORDER.indexOf(activeTab);
    const tabIndex = TAB_ORDER.indexOf(tab);

    if (tab === activeTab) return 'active';
    if (tabIndex < activeIndex) return 'left';
    return 'right';
  };

  return (
    <div dir="ltr" className="h-screen flex flex-col overflow-hidden bg-gradient-subtle">
      {/* Theme Toggle - Top Right */}
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle />
      </div>

      {/* Decorative background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-game-blue/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-game-blue/10 rounded-full blur-3xl" />
      </div>

      {/* Main Content */}
      <div className="slide-container flex-1">
        {/* PFP Panel */}
        <div className={`slide-panel ${getPanelClass('pfp')}`}>
          <div className="h-full overflow-y-auto">
            <div className={`max-w-2xl mx-auto px-4 transition-all duration-700 ease-out ${
              !headerVisible ? 'py-8' : 'py-0 h-full flex flex-col justify-center'
            }`}>
              {/* Landing Section — header + search implode together */}
              {headerVisible && (
                <div className={`${headerExiting ? 'animate-header-vanish' : ''}`}>
                  {/* Header */}
                  <div className="text-center mb-8">
                    <h1 className="font-bold text-text-dark dark:text-neutral-100 mb-3 text-4xl md:text-5xl">
                      <span className="bg-gradient-to-r from-game-blue-dark to-game-blue bg-clip-text text-transparent">
                        play with your nub
                      </span>
                    </h1>
                    <p className="text-text-muted dark:text-neutral-400 text-base md:text-lg">
                      search for your nub below!
                    </p>
                  </div>

                  {/* Search */}
                  <div className="glass-card p-4 md:p-6 rounded-2xl shadow-elevated">
                    <NFTSearchInput />
                  </div>

                  {/* Loading indicator — stays on landing page while fetching */}
                  {isLoading && (
                    <div className="text-center py-8 mt-4 animate-fade-in">
                      <div className="inline-flex items-center gap-3 text-text-muted dark:text-neutral-400">
                        <div className="w-5 h-5 border-2 border-game-blue border-t-transparent rounded-full animate-spin" />
                        <FunLoadingText />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Post-landing: compact search + loading + results */}
              {!headerVisible && (
                <>
                  {/* Compact Search */}
                  <div className="mb-6 animate-fade-in">
                    <div className="glass-card p-4 md:p-6 rounded-xl">
                      <NFTSearchInput />
                    </div>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="glass-card bg-red-50/80 dark:bg-red-900/30 border-red-200 dark:border-red-800 rounded-xl p-4 mb-6 text-center animate-fade-in">
                      <p className="text-red-600 dark:text-red-400 font-medium">{error}</p>
                    </div>
                  )}

                  {/* Loading State */}
                  {isLoading && (
                    <div className="text-center py-12">
                      <div className="inline-flex items-center gap-3 text-text-muted dark:text-neutral-400">
                        <div className="w-5 h-5 border-2 border-game-blue border-t-transparent rounded-full animate-spin" />
                        <FunLoadingText />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Memes Panel */}
        <div className={`slide-panel ${getPanelClass('memes')}`}>
          <div className="h-full overflow-y-auto px-3 py-4 sm:px-5 sm:py-5 lg:px-8 lg:py-6">
            <MemeBuilder />
          </div>
        </div>

        {/* Banner Panel */}
        <div className={`slide-panel ${getPanelClass('banner')}`}>
          <div className="h-full overflow-y-auto px-3 py-4 sm:px-5 sm:py-5 lg:px-8 lg:py-6">
            <BannerBuilder />
          </div>
        </div>
      </div>

      {/* Banner Crop Modal */}
      <BannerCropModal />
    </div>
  );
}

export default App;
